import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { GlobalStyles } from '@mui/material';
import tokens, { cssVariables } from './tokens';

/**
 * The proposal theme. Before this, the app had no MUI theme at all — every
 * surface was styled with inline `style={{}}` blocks drawing on 171 different
 * hex values. Components should now take colour, type and spacing from here.
 */
export const buildTheme = () =>
  createTheme({
    breakpoints: { values: tokens.breakpoint },

    palette: {
      mode: 'light',
      primary: {
        light: tokens.palette.brand[400],
        main: tokens.palette.brand[600],
        dark: tokens.palette.brand[800],
        contrastText: tokens.palette.text.onBrand
      },
      secondary: {
        light: tokens.palette.accent[200],
        // 600 rather than the decorative 400: white text on 400 is 3.06:1,
        // which fails WCAG AA for a filled control.
        main: tokens.palette.accent[600],
        dark: tokens.palette.accent[700],
        contrastText: tokens.palette.text.onBrand
      },
      success: {
        light: tokens.palette.success[100],
        main: tokens.palette.success[600],
        dark: tokens.palette.success[700],
        contrastText: tokens.palette.text.onBrand
      },
      warning: {
        light: tokens.palette.warning[100],
        main: tokens.palette.warning[500],
        dark: tokens.palette.warning[700],
        contrastText: tokens.palette.text.primary
      },
      error: {
        light: tokens.palette.danger[100],
        main: tokens.palette.danger[500],
        dark: tokens.palette.danger[700],
        contrastText: tokens.palette.text.onBrand
      },
      text: {
        primary: tokens.palette.text.primary,
        secondary: tokens.palette.text.secondary,
        disabled: tokens.palette.text.tertiary
      },
      divider: tokens.palette.border.subtle,
      background: {
        default: tokens.palette.surface.page,
        paper: tokens.palette.surface.raised
      },
      grey: tokens.palette.neutral
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: tokens.fonts.body,
      // Display sizes use the brand face; body sizes stay on Montserrat.
      h1: {
        fontFamily: tokens.fonts.display,
        fontSize: tokens.fontSize['5xl'],
        fontWeight: tokens.fontWeight.bold,
        lineHeight: tokens.lineHeight.tight,
        letterSpacing: tokens.letterSpacing.tighter
      },
      h2: {
        fontFamily: tokens.fonts.display,
        fontSize: tokens.fontSize['4xl'],
        fontWeight: tokens.fontWeight.bold,
        lineHeight: tokens.lineHeight.tight,
        letterSpacing: tokens.letterSpacing.tight
      },
      h3: {
        fontFamily: tokens.fonts.display,
        fontSize: tokens.fontSize['3xl'],
        fontWeight: tokens.fontWeight.semibold,
        lineHeight: tokens.lineHeight.snug,
        letterSpacing: tokens.letterSpacing.tight
      },
      h4: {
        fontSize: tokens.fontSize['2xl'],
        fontWeight: tokens.fontWeight.semibold,
        lineHeight: tokens.lineHeight.snug
      },
      h5: {
        fontSize: tokens.fontSize.xl,
        fontWeight: tokens.fontWeight.semibold,
        lineHeight: tokens.lineHeight.snug
      },
      h6: {
        fontSize: tokens.fontSize.lg,
        fontWeight: tokens.fontWeight.semibold,
        lineHeight: tokens.lineHeight.normal
      },
      subtitle1: {
        fontSize: tokens.fontSize.lg,
        fontWeight: tokens.fontWeight.medium,
        lineHeight: tokens.lineHeight.normal
      },
      subtitle2: {
        fontSize: tokens.fontSize.base,
        fontWeight: tokens.fontWeight.semibold,
        lineHeight: tokens.lineHeight.normal
      },
      body1: {
        fontSize: tokens.fontSize.base,
        lineHeight: tokens.lineHeight.relaxed
      },
      body2: {
        fontSize: tokens.fontSize.sm,
        lineHeight: tokens.lineHeight.relaxed
      },
      caption: {
        fontSize: tokens.fontSize.xs,
        lineHeight: tokens.lineHeight.normal,
        color: tokens.palette.text.secondary
      },
      overline: {
        fontSize: tokens.fontSize['2xs'],
        fontWeight: tokens.fontWeight.semibold,
        letterSpacing: tokens.letterSpacing.widest,
        textTransform: 'uppercase',
        lineHeight: tokens.lineHeight.normal
      },
      button: {
        fontSize: tokens.fontSize.sm,
        fontWeight: tokens.fontWeight.semibold,
        letterSpacing: tokens.letterSpacing.wide,
        textTransform: 'none' // sentence case reads better than SHOUTING
      }
    },

    shadows: [
      'none',
      tokens.shadow.xs,
      tokens.shadow.sm,
      tokens.shadow.sm,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.xl,
      ...Array(14).fill(tokens.shadow.xl)
    ],

    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.full,
            paddingInline: tokens.space[6],
            paddingBlock: tokens.space[3],
            minHeight: 44, // WCAG 2.5.5 minimum tap target
            transition: `all ${tokens.motion.duration.fast} ${tokens.motion.easing.standard}`,
            '&:focus-visible': { boxShadow: tokens.shadow.focus, outline: 'none' }
          },
          containedPrimary: {
            background: tokens.gradient.brand,
            boxShadow: tokens.shadow.brand,
            '&:hover': {
              boxShadow: tokens.shadow.brandLg,
              transform: 'translateY(-1px)'
            }
          },
          // An outlined button's border is the only thing marking it as a
          // control, so it needs the 3:1 token rather than a decorative one.
          outlined: {
            borderColor: tokens.palette.border.interactive
          },
          sizeLarge: {
            paddingInline: tokens.space[8],
            paddingBlock: tokens.space[4],
            fontSize: tokens.fontSize.base,
            minHeight: 52
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: tokens.palette.border.interactive
          },
          root: {
            minHeight: 44,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.palette.neutral[600]
            }
          }
        }
      },
      MuiContainer: {
        // Containers use the proposal's single gutter rather than MUI's own
        // 16/24px, so sections built on Container line up with the cover and
        // the letter, which measure from the same variable.
        styleOverrides: {
          root: {
            paddingLeft: 'var(--ly-shell-gutter, 1.5rem)',
            paddingRight: 'var(--ly-shell-gutter, 1.5rem)',
            '@media (min-width:600px)': {
              paddingLeft: 'var(--ly-shell-gutter, 1.5rem)',
              paddingRight: 'var(--ly-shell-gutter, 1.5rem)'
            }
          }
        }
      },
      MuiTableContainer: {
        // A container that scrolls must be reachable by keyboard, or its
        // content is unreachable without a pointer (WCAG 2.1.1).
        defaultProps: { tabIndex: 0 }
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { minWidth: 44, minHeight: 44 }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: { minWidth: 44, minHeight: 44 }
        }
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${tokens.palette.border.subtle}`,
            borderRadius: tokens.radius.xl
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius['2xl'],
            border: `1px solid ${tokens.palette.border.subtle}`,
            boxShadow: tokens.shadow.sm
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.full,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.xs
          }
        }
      },
      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: {
          root: {
            color: tokens.palette.text.brand,
            fontWeight: tokens.fontWeight.medium,
            '&:focus-visible': {
              boxShadow: tokens.shadow.focus,
              outline: 'none',
              borderRadius: tokens.radius.sm
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
            '&:focus-visible': { boxShadow: tokens.shadow.focus, outline: 'none' }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.palette.surface.inverse,
            fontSize: tokens.fontSize.xs,
            borderRadius: tokens.radius.md,
            padding: `${tokens.space[2]} ${tokens.space[3]}`
          }
        }
      }
    }
  });

/**
 * Emits every design token as a `--ly-*` custom property on :root, plus the
 * handful of global rules the proposal shell relies on. Keeping the tokens in
 * CSS (not only in JS) is what lets the print route — which Chromium loads to
 * produce the PDF — style itself from the exact same values.
 */
const globalStyles = (
  <GlobalStyles
    styles={{
      ':root': cssVariables(),
      '*, *::before, *::after': { boxSizing: 'border-box' },
      html: {
        WebkitTextSizeAdjust: '100%',
        textSizeAdjust: '100%',
        scrollBehavior: 'smooth'
      },
      body: {
        margin: 0,
        fontFamily: tokens.fonts.body,
        color: tokens.palette.text.primary,
        backgroundColor: tokens.palette.surface.page,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility'
      },
      'img, picture, video, canvas, svg': { display: 'block', maxWidth: '100%' },
      // Safety net against a wide element forcing the page to scroll sideways.
      // `clip` rather than `hidden` because `hidden` creates a scroll container
      // that breaks `position: sticky` on descendants — and it is applied only
      // where supported (Safari 16+), so on Safari 15 sticky keeps working and
      // we simply fall back to the layout not needing the net.
      '@supports (overflow: clip)': {
        '#root': { overflowX: 'clip' }
      },
      // Older engines without :focus-visible still get a visible focus ring
      // from the plain :focus rule; newer ones refine it to keyboard-only.
      ':focus': { outline: `2px solid ${tokens.palette.border.focus}`, outlineOffset: 2 },
      ':focus:not(:focus-visible)': { outline: 'none' },
      ':focus-visible': { outline: `2px solid ${tokens.palette.border.focus}`, outlineOffset: 2 },
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important'
        }
      }
    }}
  />
);

export default function ProposalThemeProvider({ children }) {
  const theme = useMemo(() => buildTheme(), []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      {children}
    </ThemeProvider>
  );
}

export { tokens };
